import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, SEO } from '../../components/common';
import { FiSend, FiCheckCircle } from 'react-icons/fi';
import routes from '../../utils/routes';
import { getAuthToken } from '../../utils/localStorage';
import { submitSupportTicket } from '../../api/auth';

const CATEGORIES = [
  'Account',
  'Payment',
  'Copilot/AI',
  'Buckets',
  'Expert Sessions',
  'Bug Report',
  'Other',
];

const SupportTicket: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!getAuthToken()) {
      navigate(routes.login);
    }
  }, [navigate]);

  const handleSubmit = async () => {
    // Validation
    if (!subject.trim() || subject.trim().length < 3) {
      setError('Subject must be at least 3 characters');
      return;
    }
    if (!description.trim() || description.trim().length < 20) {
      setError('Description must be at least 20 characters');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        navigate(routes.login);
        return;
      }
      await submitSupportTicket(token, {
        subject: subject.trim(),
        category,
        description: description.trim(),
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to submit ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-6"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: colors.green + '20' }}
        >
          {React.createElement(FiCheckCircle as any, { size: 40, color: colors.green })}
        </div>
        <CText type="B24" color={colors.textColor} style={{ marginBottom: 8, textAlign: 'center' }}>
          Ticket Submitted
        </CText>
        <CText type="r14" color={colors.grayScale500} style={{ textAlign: 'center', marginBottom: 24 }}>
          We've received your support ticket and will get back to you shortly via email.
        </CText>
        <button
          onClick={() => navigate(routes.home)}
          className="px-8 py-3 rounded-xl font-medium"
          style={{ backgroundColor: colors.primary, color: colors.white }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pb-4"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <SEO title="Submit a Support Ticket" noIndex />
      {/* Gradient Header */}
      <div
        className="p-6 lg:p-8 pb-8"
        style={{
          background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 100%)`,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="mb-4 text-white opacity-80 hover:opacity-100"
        >
          &larr; Back
        </button>
        <h1 className="text-2xl font-bold m-0 mb-2" style={{ color: colors.white }}>
          Submit a Support Ticket
        </h1>
        <p className="text-sm m-0" style={{ color: colors.white, opacity: 0.8 }}>
          Describe your issue and we'll help you out
        </p>
      </div>

      <div className="p-4 lg:p-8 -mt-4">
        <div className="lg:max-w-3xl lg:mx-auto space-y-5">
          {/* Subject */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: colors.textColor }}
            >
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of your issue"
              maxLength={200}
              className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors"
              style={{
                backgroundColor: colors.inputBackground,
                borderColor: colors.borderColor,
                color: colors.textColor,
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = colors.borderColor; }}
            />
          </div>

          {/* Category */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: colors.textColor }}
            >
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors appearance-none"
              style={{
                backgroundColor: colors.inputBackground,
                borderColor: colors.borderColor,
                color: colors.textColor,
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = colors.borderColor; }}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: colors.textColor }}
            >
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe your issue in detail (min 20 characters)"
              maxLength={5000}
              rows={6}
              className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors resize-none"
              style={{
                backgroundColor: colors.inputBackground,
                borderColor: colors.borderColor,
                color: colors.textColor,
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = colors.borderColor; }}
            />
            <p className="text-xs mt-1" style={{ color: colors.grayScale500 }}>
              {description.length}/5000
            </p>
          </div>

          {/* Error */}
          {error && (
            <div
              className="px-4 py-3 rounded-xl text-sm"
              style={{ backgroundColor: colors.alertColor + '15', color: colors.alertColor }}
            >
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3.5 rounded-xl font-medium flex items-center justify-center gap-2 transition-opacity"
            style={{
              backgroundColor: colors.primary,
              color: colors.white,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? (
              <span>Submitting...</span>
            ) : (
              <>
                {React.createElement(FiSend as any, { size: 18 })}
                <span>Submit Ticket</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupportTicket;
